import { Link } from 'react-router-dom';
import type { ContentItem } from '@/types/content';
import SectionHeading from '@/components/feature/SectionHeading';

interface HomeNewsProps {
  news: ContentItem[];
  ranking: ContentItem[];
}

export default function HomeNews({ news, ranking }: HomeNewsProps) {
  return (
    <section className="w-full px-4 py-10 md:px-8 md:py-14">
      <SectionHeading
        eyebrow="Newsroom"
        title="最新影视资讯快报"
        description="行业动态、明星消息、影评解析，每天更新，五分钟了解今日影视圈。"
        moreTo="/news"
        moreLabel="进入资讯频道"
      />

      <div className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-[1.8fr_1fr]">
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {news.map((item, index) => (
            <Link
              key={item.id}
              to={`/detail/${item.id}`}
              className={`group flex cursor-pointer flex-col gap-3 px-4 py-4 transition-colors hover:bg-background-100 sm:flex-row sm:items-center sm:gap-5 md:px-5 ${
                index !== news.length - 1 ? 'border-b border-background-200' : ''
              }`}
            >
              <div className="flex w-16 shrink-0 flex-col items-center justify-center rounded-md bg-background-100 py-2 transition-colors group-hover:bg-primary-50">
                <span className="font-heading text-lg font-bold leading-none text-foreground-950 group-hover:text-primary-600">
                  {item.date?.slice(8, 10)}
                </span>
                <span className="mt-1 font-label text-[10px] tracking-wide text-foreground-500">
                  {item.date?.slice(0, 7)}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 font-label text-[10px] font-medium text-secondary-800">
                    {item.articleCategory}
                  </span>
                  <span className="flex items-center gap-1 font-label text-[11px] text-foreground-400">
                    <i className="ri-eye-line text-[12px]"></i>
                    {(item.heat ?? 60) * 132}
                  </span>
                </div>
                <h3 className="mt-2 line-clamp-2 font-heading text-sm font-semibold leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-600 md:text-[15px]">
                  {item.title}
                </h3>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600 sm:line-clamp-1">
                  {item.summary}
                </p>
              </div>
              <i className="ri-arrow-right-line hidden text-[16px] text-foreground-300 transition-all group-hover:translate-x-1 group-hover:text-primary-500 sm:block"></i>
            </Link>
          ))}
        </div>

        <aside className="rounded-lg border border-background-200 bg-background-100 p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold text-foreground-950">本周口碑榜</h3>
            <span className="flex items-center gap-1 font-label text-[10px] uppercase tracking-[0.16em] text-primary-600">
              <i className="ri-line-chart-line text-[12px]"></i>
              Top 5
            </span>
          </div>
          <ol className="mt-4 space-y-1">
            {ranking.map((item, index) => (
              <li key={item.id}>
                <Link
                  to={`/detail/${item.id}`}
                  className="group flex cursor-pointer items-center gap-3 rounded-md px-1.5 py-2.5 transition-colors hover:bg-background-50"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-sm font-label text-[11px] font-bold ${
                      index < 2
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-300/70 text-foreground-700'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block truncate font-label text-[10px] text-foreground-500">
                      {item.genre.slice(0, 2).join(' / ')} · {item.year}
                    </span>
                  </span>
                  <span className="flex items-center gap-0.5 font-label text-[11px] font-semibold text-primary-600">
                    <i className="ri-star-fill text-[9px] text-accent-500"></i>
                    {item.rating?.toFixed(1)}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
          <Link
            to="/movies"
            className="mt-4 flex cursor-pointer items-center justify-center gap-1.5 rounded-md border border-background-300 bg-background-50 py-2 font-label text-[11px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            查看完整榜单
            <i className="ri-arrow-right-line text-[12px]"></i>
          </Link>
        </aside>
      </div>
    </section>
  );
}